import { useState } from 'react';
import { AppTab } from '@freight-cbm/shared-types';
import { useAppStore } from '@/store/store';
import { AboutModal, buildAboutConfig } from '@ascend/about-modal';
import appIdentity from 'virtual:app-identity';
import { Header } from './Header';
import { TabBar } from './TabBar';
import { ThemeProvider } from '@/components/shared/ThemeProvider';
import { SettingsModal } from '@/components/shared/SettingsModal';
import { FeedbackForm } from '@/components/FeedbackForm';
import { CalculatorScreen } from '@/screens/CalculatorScreen';
import { OptimizationScreen } from '@/screens/OptimizationScreen';
import { ReferenceScreen } from '@/screens/ReferenceScreen';
import { HistoryScreen } from '@/screens/HistoryScreen';

interface ScreenProps {
  onErrorReport?: () => void;
}

const screens: Record<AppTab, React.FC<ScreenProps>> = {
  calculator: CalculatorScreen,
  optimization: OptimizationScreen,
  reference: ReferenceScreen,
  history: HistoryScreen,
};

export function AppLayout() {
  const [activeTab, setActiveTab] = useState<AppTab>('calculator');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const theme = useAppStore((s) => s.preferences.theme);

  const aboutConfig = buildAboutConfig(appIdentity, 'VITE_APP_');

  const Screen = screens[activeTab];

  const handleDemoLoad = () => {
    setActiveTab('calculator');
  };

  return (
    <>
      <ThemeProvider theme={theme} />
      <a href="#main-content" className="skip-to-content" onClick={(e) => { e.preventDefault(); (document.querySelector('[role="tabpanel"]') as HTMLElement | null)?.focus(); }}>
        Skip to content
      </a>
      <Header onSettingsClick={() => setSettingsOpen(true)} onDemoLoad={handleDemoLoad} onFeedbackClick={() => setFeedbackOpen(true)} />
      <TabBar activeTab={activeTab} onTabChange={setActiveTab} />
      <main
        id={`panel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`tab-${activeTab}`}
        tabIndex={-1}
        style={{
          flex: 1,
          maxWidth: 'var(--max-content-width)',
          width: '100%',
          margin: '0 auto',
          padding: 'var(--space-4)',
          outline: 'none',
        }}
      >
        <Screen onErrorReport={() => setFeedbackOpen(true)} />
      </main>
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} onAboutClick={() => { setSettingsOpen(false); setAboutOpen(true); }} />
      <FeedbackForm open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
      <AboutModal isOpen={aboutOpen} onClose={() => setAboutOpen(false)} config={aboutConfig} />
    </>
  );
}
